// biome-ignore-all lint/a11y/noLabelWithoutControl: staff-only admin surface; these labels are visual captions for the adjacent control, intentionally not wired via htmlFor (Task #25).
// =============================================================================
// TemplatesAdmin — manage comment templates (and embeds NextStepsAdmin etc.)
// =============================================================================
// Renders: a tabbed editor with six tabs:
//   - Comment Templates (CRUD with category + body, lives in this file)
//   - Action Sheet Templates (NextStepsAdmin)
//   - Standard Costs (StandardCostsAdmin)
//   - Banners (BannersAdmin)
//   - Onboarding Emails (OnboardingEmailsAdmin)
//   - Help Content (HelpContentAdmin)
// Session:
//   user - admin user (for audit logging), read from useSession
//
// Pulled out of AdminPages.jsx as part of the Admin file split (May 2026).
// =============================================================================
import React from "react";
import { useSession } from "../../app/SessionContext.jsx";
import { insertCommentTemplates, updateCommentTemplateById } from "../../data/commentTemplates";
import { useCommentTemplates } from "../../hooks/useCommentTemplates";
import { logAudit } from "../../utils/auditLog";
import { Btn, Card } from "../../utils/ui.jsx";
import { ModalBackdrop } from "../form/FormUI.jsx";
import { BannersAdmin } from "./BannersAdmin.jsx";
import { HelpContentAdmin } from "./HelpContentAdmin.jsx";
import { NextStepsAdmin } from "./NextStepsAdmin.jsx";
import { OnboardingEmailsAdmin } from "./OnboardingEmailsAdmin.jsx";
import { StandardCostsAdmin } from "./StandardCostsAdmin.jsx";
import { SystemSettingsAdmin } from "./SystemSettingsAdmin.jsx";

export function TemplatesAdmin() {
  // `user` now comes from SessionContext rather than a prop (Phase 3 refactor).
  const { user } = useSession();
  // Admin sees ALL templates including soft-deleted ones so they can reactivate.
  var _db = useCommentTemplates({ includeDeactivated: true });
  var templates = _db.templates;
  var _dbLoading = _db.loading;
  var reloadTemplates = _db.reload;
  // Persist the active admin tab in sessionStorage so a refresh (which remounts
  // this screen) keeps you on the same tab instead of snapping back to the first.
  const [adminTab, setAdminTabRaw] = React.useState(function () {
    try {
      return sessionStorage.getItem("zephyr_admin_tab") || "templates";
    } catch (_e) {
      return "templates";
    }
  });
  function setAdminTab(v) {
    try {
      sessionStorage.setItem("zephyr_admin_tab", v);
    } catch (_e) {}
    setAdminTabRaw(v);
  }
  const [cat, setCat] = React.useState("All");
  const [search, setSearch] = React.useState("");
  const [editing, setEditing] = React.useState(null);
  const [form, setForm] = React.useState({ category: "General", title: "", text: "" });
  const [confirmDel, setConfirmDel] = React.useState(null);
  const [saving, setSaving] = React.useState(false);

  const EDITABLE_CATS = Array.from(
    new Set(
      templates.map(function (t) {
        return t.category;
      })
    )
  );

  const filtered = templates.filter(function (t) {
    var matchCat = cat === "All" || t.category === cat;
    var q = search.toLowerCase();
    var matchQ =
      !q ||
      t.title.toLowerCase().includes(q) ||
      t.text.toLowerCase().includes(q) ||
      t.category?.toLowerCase().includes(q);
    return matchCat && matchQ;
  });

  function startEdit(t) {
    setEditing(t.id);
    setForm({
      category: t.category,
      title: t.title,
      text: t.text,
      appends_next_steps: t.appends_next_steps || "",
    });
  }

  function startNew() {
    setEditing("new");
    setForm({
      category: cat !== "All" ? cat : "General",
      title: "",
      text: "",
      appends_next_steps: "",
    });
  }

  function saveEdit() {
    if (!form.title.trim()) return;
    setSaving(true);
    var row = {
      category: form.category,
      title: form.title,
      text: form.text,
      appends_next_steps: form.appends_next_steps || "",
    };
    if (editing === "new") {
      var newId = `custom_${Date.now()}`;
      insertCommentTemplates([Object.assign({ id: newId }, row)]).then(function () {
        logAudit("comment_template_created", {
          id: newId,
          category: row.category,
          title: row.title,
          by: user?.email || "",
        });
        reloadTemplates();
        setSaving(false);
        setEditing(null);
      });
    } else {
      var editedId = editing;
      updateCommentTemplateById(editedId, row).then(function () {
        logAudit("comment_template_updated", {
          id: editedId,
          category: row.category,
          title: row.title,
          by: user?.email || "",
        });
        reloadTemplates();
        setSaving(false);
        setEditing(null);
      });
    }
  }

  function doDelete(id) {
    // Task #DB-2: soft-delete via deactivated=true.
    updateCommentTemplateById(id, { deactivated: true }).then(function () {
      logAudit("comment_template_deactivated", { id: id, by: user?.email || "" });
      reloadTemplates();
      setConfirmDel(null);
    });
  }

  function reactivateTemplate(id) {
    updateCommentTemplateById(id, { deactivated: false }).then(function () {
      logAudit("comment_template_reactivated", { id: id, by: user?.email || "" });
      reloadTemplates();
    });
  }

  const allCats = [
    "All",
    ...Array.from(
      new Set(
        templates.map(function (t) {
          return t.category;
        })
      )
    ),
  ];
  return (
    <div className="p-6 max-w-[1100px] mx-auto">
      <h1 className="m-0 mb-4 text-[22px] text-zephyr-text">System Content</h1>

      {/* Tab bar */}
      <div className="flex gap-0 mb-6 border-b-2 border-zephyr-border">
        {[
          { key: "templates", label: "Comment Templates" },
          { key: "nextsteps", label: "Action Sheet Templates" },
          { key: "costs", label: "Standard Costs" },
          { key: "banners", label: "Banners" },
          { key: "onboarding", label: "Automated Emails" },
          { key: "help", label: "Help Content" },
          { key: "system", label: "Monitoring" },
        ].map(function (t) {
          var active = adminTab === t.key;
          return (
            <button
              key={t.key}
              onClick={function () {
                setAdminTab(t.key);
              }}
              className={[
                "bg-transparent border-0 py-[10px] px-5 text-[14px] cursor-pointer -mb-[2px]",
                active
                  ? "border-b-[3px] border-b-zephyr-teal font-bold text-zephyr-teal"
                  : "border-b-[3px] border-b-transparent font-normal text-zephyr-muted",
              ].join(" ")}
              style={{ fontFamily: "inherit" }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Standard Costs tab */}
      {adminTab === "costs" && <StandardCostsAdmin inline user={user} />}

      {adminTab === "banners" && <BannersAdmin />}

      {adminTab === "onboarding" && <OnboardingEmailsAdmin />}

      {adminTab === "help" && <HelpContentAdmin />}

      {adminTab === "system" && <SystemSettingsAdmin user={user} />}

      {/* Action Sheet (Next Steps) Templates tab */}
      {adminTab === "nextsteps" && <NextStepsAdmin user={user} />}

      {/* Comment Templates tab */}
      {adminTab === "templates" && (
        <div>
          <div className="flex justify-between items-center mb-[6px]">
            <div>
              <h2 className="m-0 text-[18px] text-zephyr-text">Comment Templates</h2>
              <p className="mt-1 mb-0 text-[13px] text-zephyr-muted">
                Changes are saved permanently to the database.
              </p>
            </div>
            <Btn onClick={startNew}>+ New Template</Btn>
          </div>

          {/* Filter bar */}
          <Card style={{ marginBottom: 16, padding: 14 }}>
            <div className="flex gap-[10px] flex-wrap items-center">
              <input
                placeholder="Search templates..."
                value={search}
                onChange={function (e) {
                  setSearch(e.target.value);
                }}
                className="flex-1 min-w-[180px] py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
                style={{ fontFamily: "inherit" }}
              />
              <select
                value={cat}
                onChange={function (e) {
                  setCat(e.target.value);
                }}
                className="py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
                style={{ fontFamily: "inherit" }}
              >
                {allCats.map(function (c) {
                  return <option key={c}>{c}</option>;
                })}
              </select>
              <Btn
                variant="secondary"
                onClick={function () {
                  setSearch("");
                  setCat("All");
                }}
              >
                Clear
              </Btn>
            </div>
            <div className="mt-2 text-[13px] text-zephyr-muted">
              {filtered.length} template{filtered.length !== 1 ? "s" : ""}
            </div>
          </Card>

          {/* Template list grouped by category */}
          {allCats
            .filter(function (c) {
              return c !== "All" && (cat === "All" || cat === c);
            })
            .map(function (category) {
              var catItems = filtered.filter(function (t) {
                return t.category === category;
              });
              if (catItems.length === 0) return null;
              return (
                <div key={category} className="mb-5">
                  <div className="text-xs font-bold text-white bg-zephyr-teal-dark py-[6px] px-[14px] rounded-t-[8px] inline-block">
                    {category}
                  </div>
                  <div className="border border-zephyr-border rounded-b-[8px] rounded-tr-[8px] overflow-hidden">
                    {catItems.map(function (t, idx) {
                      return (
                        <div
                          key={t.id}
                          style={{
                            padding: "14px 18px",
                            borderBottom: idx < catItems.length - 1 ? "1px solid #CFE0DE" : "none",
                            background: t.deactivated
                              ? "#FFF3F3"
                              : idx % 2 === 0
                                ? "#FFFFFF"
                                : "#F7F9F9",
                            opacity: t.deactivated ? 0.6 : 1,
                          }}
                        >
                          <div className="flex justify-between items-start gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-bold text-[14px] text-zephyr-text">
                                  {t.title}
                                </span>
                                {t.deactivated && (
                                  <span className="text-[11px] font-bold text-white bg-[#C62828] rounded px-2 py-[2px]">
                                    DEACTIVATED
                                  </span>
                                )}
                              </div>
                              <div className="text-[13px] text-zephyr-muted leading-relaxed">
                                {t.text}
                              </div>
                            </div>
                            <div className="flex gap-[6px] flex-shrink-0">
                              {!t.deactivated && (
                                <button
                                  onClick={function () {
                                    startEdit(t);
                                  }}
                                  className="bg-transparent border border-zephyr-border rounded-[6px] py-[5px] px-3 text-[12px] cursor-pointer text-zephyr-text"
                                  style={{ fontFamily: "inherit" }}
                                >
                                  Edit
                                </button>
                              )}
                              {t.deactivated ? (
                                <button
                                  onClick={function () {
                                    reactivateTemplate(t.id);
                                  }}
                                  className="bg-transparent border border-zephyr-teal rounded-[6px] py-[5px] px-3 text-[12px] cursor-pointer text-zephyr-teal"
                                  style={{ fontFamily: "inherit" }}
                                >
                                  Reactivate
                                </button>
                              ) : (
                                <button
                                  onClick={function () {
                                    setConfirmDel(t.id);
                                  }}
                                  className="bg-transparent border border-[#EF5350] rounded-[6px] py-[5px] px-3 text-[12px] cursor-pointer text-[#C62828]"
                                  style={{ fontFamily: "inherit" }}
                                >
                                  Deactivate
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

          {/* Edit / New modal */}
          {editing !== null && (
            <ModalBackdrop
              onClose={function () {
                setEditing(null);
              }}
              contentStyle={{
                background: "#FFFFFF",
                borderRadius: 14,
                width: "calc(100vw - 40px)",
                maxWidth: 560,
                boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
                padding: 24,
              }}
            >
              <div className="font-bold text-[16px] text-zephyr-teal-dark mb-[18px]">
                {editing === "new" ? "New Template" : "Edit Template"}
              </div>
              <div className="mb-3">
                <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
                  Category
                </label>
                <select
                  value={form.category}
                  onChange={function (e) {
                    setForm(function (f) {
                      return Object.assign({}, f, { category: e.target.value });
                    });
                  }}
                  className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
                  style={{ fontFamily: "inherit" }}
                >
                  {EDITABLE_CATS.map(function (c) {
                    return <option key={c}>{c}</option>;
                  })}
                </select>
              </div>
              <div className="mb-3">
                <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
                  Title
                </label>
                <input
                  value={form.title}
                  onChange={function (e) {
                    setForm(function (f) {
                      return Object.assign({}, f, { title: e.target.value });
                    });
                  }}
                  placeholder="Short descriptive title..."
                  className="w-full box-border py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
                  style={{ fontFamily: "inherit" }}
                />
              </div>
              <div className="mb-5">
                <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
                  Template Text
                </label>
                <textarea
                  value={form.text}
                  onChange={function (e) {
                    setForm(function (f) {
                      return Object.assign({}, f, { text: e.target.value });
                    });
                  }}
                  rows={5}
                  placeholder="Enter the template text..."
                  className="w-full box-border py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white resize-y"
                  style={{ fontFamily: "inherit" }}
                />
              </div>
              <div className="flex gap-[10px] justify-end">
                <Btn
                  variant="secondary"
                  onClick={function () {
                    setEditing(null);
                  }}
                >
                  Cancel
                </Btn>
                <Btn
                  onClick={saveEdit}
                  disabled={!form.title.trim() || !form.text.trim() || saving}
                >
                  {saving ? "Saving..." : "Save Template"}
                </Btn>
              </div>
            </ModalBackdrop>
          )}

          {/* Delete confirm modal */}
          {confirmDel && (
            <div className="fixed inset-0 bg-black/[0.45] z-[9999] flex items-center justify-center p-5">
              <div className="bg-white rounded-[14px] max-w-[400px] w-full p-6 shadow-[0_8px_40px_rgba(0,0,0,0.2)]">
                <div className="font-bold text-[16px] mb-[10px]">Deactivate Template?</div>
                <div className="text-[13px] text-zephyr-muted mb-5">
                  Existing requests that already used this template keep their text. The template
                  will be hidden from the picker for new comments. You can reactivate it later from
                  this screen.
                </div>
                <div className="flex gap-[10px] justify-end">
                  <Btn
                    variant="secondary"
                    onClick={function () {
                      setConfirmDel(null);
                    }}
                  >
                    Cancel
                  </Btn>
                  <Btn
                    onClick={function () {
                      doDelete(confirmDel);
                    }}
                    style={{ background: "#C62828", color: "white" }}
                  >
                    Deactivate
                  </Btn>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
